/**
 * <AccountAvatarAIModal> — « Génère ton avatar IA » (template).
 *
 * L'ancienne `GenerateAIAvatarModal`, geste pour geste : prendre une photo de son visage ou en
 * choisir une, la revoir (« Choisir une autre photo »), « Générer avec ma photo », « Annuler », et
 * la ligne qui dit où l'élève en est de ses générations du mois. Un refus du serveur (la limite du
 * mois, une erreur) se lit dans la feuille, au-dessus du bouton ; le reste de l'issue (« Génération
 * lancée », « Avatar prêt ! », une photo refusée) se dit sur la page, la feuille refermée.
 *
 * Contrôlé : la photo choisie, l'envoi, la génération en cours, ce qui reste et le refus arrivent
 * en props ; la caméra et la galerie sont des effets que l'écran injecte (`onTakePhoto`,
 * `onPickPhoto`). `hidden` garde la feuille montée pendant que l'appareil photo de l'app passe
 * par-dessus (`<Modal hidden>`).
 *
 * Composes DS pieces only — `<Modal>`, `<Banner>`, `<DashedButton>`, `<Avatar>`, `<TextButton>`,
 * `<Button>`, `<Text>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { DashedButton } from "../../atoms/DashedButton"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"

/** Où en est l'élève de ses générations du mois — une phrase, et si elle bloque. */
export type UIAvatarGenerationInfo = { text: string; blocking: boolean }

export type AccountAvatarAIModalProps = {
    visible: boolean
    onClose: () => void
    /** La photo choisie (une adresse locale). `null` : les deux façons d'en avoir une. */
    photoUri: string | null
    /** « Prendre une photo » : l'appareil photo de l'app. */
    onTakePhoto: () => void
    /** « Choisir une photo », et « Choisir une autre photo ». */
    onPickPhoto: () => void
    onGenerate: () => void
    /** La photo part : le bouton tourne. Défaut `false`. */
    sending?: boolean
    /** Le bouton ne répond pas : une génération tourne déjà, ou la limite du mois est atteinte. */
    generateDisabled?: boolean
    info: UIAvatarGenerationInfo
    /** Le serveur a refusé : sa phrase, au-dessus du bouton. */
    error?: string | null
    /** Caché sans être démonté, le temps d'un passage par l'appareil photo. Défaut `false`. */
    hidden?: boolean
    testID?: string
}

const TEXTS = {
    title: "Génère ton avatar IA",
    heading: "Prends une photo de ton visage",
    instructions:
        "Prends une photo claire de ton visage seul(e) et de face. Ta photo sera analysée instantanément puis automatiquement supprimée.",
    takePhoto: "Prendre une photo",
    pickPhoto: "Choisir une photo",
    changePhoto: "Choisir une autre photo",
    generate: "Générer avec ma photo",
    cancel: "Annuler",
}

/** La photo revue, en grand : la taille de l'ancienne modale. */
const PREVIEW_SIZE = 150
/** Les deux tuiles de choix : assez hautes pour leur glyphe et deux lignes. */
const CHOICE_HEIGHT = 120

export function AccountAvatarAIModal(props: AccountAvatarAIModalProps) {
    const {
        visible,
        onClose,
        photoUri,
        onTakePhoto,
        onPickPhoto,
        onGenerate,
        sending = false,
        generateDisabled = false,
        info,
        error,
        hidden = false,
        testID,
    } = props

    const footer = (
        <Stack gap="2" align="center">
            {error != null && error !== "" && (
                <Banner message={error} color="danger" icon="exclamation-circle" tinted align="start" />
            )}
            <Button
                label={TEXTS.generate}
                onPress={onGenerate}
                loading={sending}
                disabled={photoUri == null || generateDisabled}
                fullWidth
            />
            <TextButton label={TEXTS.cancel} onPress={onClose} />
        </Stack>
    )

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={TEXTS.title}
            sizeToContent
            hidden={hidden}
            footer={footer}
            testID={testID}
        >
            <Stack gap="4">
                <Stack gap="2">
                    <Text weight="bold" color="text-strong">
                        {TEXTS.heading}
                    </Text>
                    <Banner message={TEXTS.instructions} color="info" icon="info-circle" tinted align="start" />
                </Stack>

                {photoUri != null ? (
                    <Stack gap="2" align="center">
                        <Avatar uri={photoUri} size={PREVIEW_SIZE} />
                        <TextButton label={TEXTS.changePhoto} onPress={onPickPhoto} />
                    </Stack>
                ) : (
                    <View style={styles.choices}>
                        <View style={styles.choice}>
                            <DashedButton
                                label={TEXTS.takePhoto}
                                iconName="camera"
                                minHeight={CHOICE_HEIGHT}
                                onPress={onTakePhoto}
                            />
                        </View>
                        <View style={styles.choice}>
                            <DashedButton
                                label={TEXTS.pickPhoto}
                                iconName="file-image"
                                minHeight={CHOICE_HEIGHT}
                                onPress={onPickPhoto}
                            />
                        </View>
                    </View>
                )}

                <Banner
                    message={info.text}
                    color={info.blocking ? "danger" : "info"}
                    icon="info-circle"
                    tinted
                    align="start"
                />
            </Stack>
        </Modal>
    )
}

const styles = StyleSheet.create((theme) => ({
    choices: {
        flexDirection: "row",
        gap: theme.spacing["3"],
    },
    choice: {
        flex: 1,
        minWidth: 0,
    },
}))
